import React from 'react'
import { APIEntity } from 'services/api/entities'
import { TailoredProduct } from 'services/api/entities/catalog/types'
import { useKGetList } from 'services/hooks/useKGetList'
import { WithId, WithIdAndName } from 'types/common'
import { ActivityStatus } from 'types/form'

export type FilterFn<T> = (p: T) => boolean

export type CustomReferenceInputProps<T extends WithId> = {
  children: JSX.Element
  entity: APIEntity
  additionalChoices?: WithIdAndName[]
  filterFns?: FilterFn<T>[]
}

export type WithSectionAndStatus = {
  sections?: unknown[]
  status?: ActivityStatus
}

/**
 * If the product has section there should be at least one section
 * If the product has status the status should not be inactive
 * Else the product is visible in store
 * @param product the product to check
 * @returns true if is visible in store false else
 */
export const isProductVisibleInStore = <
  T extends WithSectionAndStatus & WithId
>(
  product: T
) =>
  (!product.sections || !!product.sections?.length) &&
  product.status !== 'inactive'

export const isProductVisibleForPackage = <
  T extends WithSectionAndStatus &
    WithId &
    Omit<TailoredProduct, 'clientsFull' | 'organizationsFull'>
>(
  product: T
) =>
  isProductVisibleInStore(product) ||
  product.customers.length > 0 ||
  product.organizations.length > 0

/*
  NB: This component DOES NOT WORK with AutocompleteInputs
  Do not use it for such cases until issue resolved
*/
const CustomReferenceInput = <T extends WithId>({
  children,
  entity,
  filterFns = [],
  additionalChoices = [],
  ...props
}: CustomReferenceInputProps<T>) => {
  // choices are fetched and filtered
  const fetchedChoices = useKGetList<T>(entity).filterEvery(filterFns)

  const choices = [...fetchedChoices, ...additionalChoices]

  if (!choices.length) return null

  return (
    <>
      {React.cloneElement(children, {
        choices,
        ...props,
      })}
    </>
  )
}

export default CustomReferenceInput
